// apps/client/src/ui-kit/Button.tsx
// 3D Chunky Game Buttons — Beelucky Sweepstakes Style
// Pill-shaped, tactile, with shimmer effect on primary

import { motion, HTMLMotionProps } from 'framer-motion';
import Icon from './Icon';
import { useAudio } from '../providers/AudioProvider';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    children: React.ReactNode;
    icon?: string; // Material Symbols icon name
    iconPosition?: 'left' | 'right';
    isLoading?: boolean;
    fullWidth?: boolean;
}

const variants: Record<ButtonVariant, string> = {
    primary: `
    bg-gradient-to-b from-[#FBBF24] via-[#F5A623] to-[#F59E0B]
    text-[#1a0b2e]
    shadow-[0_4px_0_#B97A00,inset_0_2px_0_rgba(255,255,255,0.4)]
    hover:from-[#FDE68A] hover:via-[#FBBF24] hover:to-[#F5A623]
    active:shadow-[0_0_0_#B97A00,inset_0_2px_0_rgba(255,255,255,0.4)] active:translate-y-[2px]
  `,
    secondary: `
    bg-transparent
    border-2 border-[#FBBF24]
    text-[#FBBF24]
    hover:bg-[#FBBF24]/10
    active:bg-[#FBBF24]/20
  `,
    danger: `
    bg-gradient-to-b from-[#FF5252] via-[#E53935] to-[#C62828]
    text-white
    shadow-[0_4px_0_#8B0000,inset_0_2px_0_rgba(255,255,255,0.3)]
    hover:from-[#FF6B6B] hover:via-[#EF5350] hover:to-[#E53935]
    active:shadow-[0_0_0_#8B0000,inset_0_2px_0_rgba(255,255,255,0.3)] active:translate-y-[2px]
  `,
    ghost: `
    bg-transparent
    text-white/60
    hover:text-white hover:bg-white/10
    active:bg-white/20
  `,
};

const sizes: Record<ButtonSize, string> = {
    sm: 'px-4 py-2 text-xs rounded-pill',
    md: 'px-6 py-3 text-sm rounded-pill',
    lg: 'px-8 py-4 text-lg rounded-pill',
};

export const Button: React.FC<ButtonProps> = ({
    variant = 'primary',
    size = 'md',
    children,
    icon,
    iconPosition = 'left',
    isLoading = false,
    fullWidth = false,
    className = '',
    disabled,
    onClick,
    ...props
}) => {
    const { playClick } = useAudio();
    const iconSize = size === 'lg' ? 'text-2xl' : size === 'md' ? 'text-xl' : 'text-base';

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (!disabled && !isLoading) {
            playClick();
        }
        if (onClick) {
            onClick(e);
        }
    };

    return (
        <motion.button
            whileTap={disabled || isLoading ? {} : { scale: 0.98 }}
            disabled={disabled || isLoading}
            className={`
        relative overflow-hidden
        font-black uppercase tracking-wide
        flex items-center justify-center gap-2
        transition-all duration-150 ease-out
        ${variants[variant]}
        ${sizes[size]}
        ${fullWidth ? 'w-full' : ''}
        ${disabled ? 'opacity-50 cursor-not-allowed grayscale' : 'cursor-pointer'}
        ${className}
      `}
            onClick={handleClick}
            {...props}
        >
            {/* Shimmer Effect for Primary */}
            {variant === 'primary' && !disabled && (
                <span className="absolute inset-0 overflow-hidden pointer-events-none">
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full animate-[shimmer_3s_infinite]" />
                </span>
            )}

            {/* Content */}
            <span className="relative flex items-center gap-2">
                {isLoading ? (
                    <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                    <>
                        {icon && iconPosition === 'left' && (
                            <Icon name={icon} className={iconSize} />
                        )}
                        {children}
                        {icon && iconPosition === 'right' && (
                            <Icon name={icon} className={iconSize} />
                        )}
                    </>
                )}
            </span>
        </motion.button>
    );
};
